import { Button } from "@istari-frontend/ui";
import { AlertTriangle, FileText, FileWarning } from "lucide-react";
import React, { lazy, Suspense, useCallback, useState } from "react";

import { TEXT_FILE_DISPLAY_MAX_SIZE } from "~/constants";
import { fileTransferConfig } from "~/resource/data-source/file-transfer-config";
import { ThemedLoadingSpinner } from "~/theming/feature/themed-loading-spinner";
import { formatBytes } from "~/utils/format-bytes";
import { useIsProbablyText } from "~/viewer/feature/use-is-probably-text";

import { ContentErrorBoundary } from "../content-error-boundary";
import { FileTypeIcon } from "../file-type-icon";
import { StatusCard } from "../status-card";
import { FileProperties } from "./file-properties";
import { getFileStatus } from "./get-file-status";
import { FileType, getFileType } from "./get-file-type";
import { HtmlFileViewer } from "./html-file-viewer";
import { ImageFileViewer } from "./image-file-viewer";
import { JsonViewMode } from "./json-view-mode";
import { JsonViewer } from "./json-viewer";
import { SelectionAction } from "./selection-action";
import { TextFileViewer } from "./text-file-viewer";
import { UnsupportedViewer } from "./unsupported-viewer";
import { formatUrlFragment, ViewerPrefs } from "./url-fragment";
import { useParsedInitialView } from "./use-parsed-initial-view";
import type { ViewConfig } from "./view-config";
import { XmlFileViewer } from "./xml-file-viewer";

const DocxFileViewer = lazy(() => import("./docx-file-viewer").then((m) => ({ default: m.DocxFileViewer })));
const MarkdownFileViewer = lazy(() =>
  import("./markdown-file-viewer").then((m) => ({ default: m.MarkdownFileViewer })),
);
const CsvFileViewer = lazy(() => import("./csv-file-viewer").then((m) => ({ default: m.CsvFileViewer })));
const PdfFileViewer = lazy(() => import("./pdf-file-viewer").then((m) => ({ default: m.PdfFileViewer })));
const ThreeDFileViewer = lazy(() => import("./three-d-file-viewer").then((m) => ({ default: m.ThreeDFileViewer })));

export interface FileViewerProps {
  content: string | Uint8Array | null;
  properties: FileProperties;
  loading?: boolean;
  versionId?: string;
  initialView?: ViewerPrefs;
  urlFragment?: string;
  onViewChange?: (fragment: string) => void;
  onSelectionAction?: (action: SelectionAction) => void;
  viewConfig?: ViewConfig | null;
  lineRange?: { startLine: number; endLine: number } | null;
  jsonViewMode?: JsonViewMode | null;
  s3Url?: string | null;
}

export function FileViewer(props: FileViewerProps) {
  return (
    <ContentErrorBoundary
      title="Couldn't load preview"
      description="Something went wrong while rendering this file."
      resetKeys={[props.content, props.versionId]}
    >
      <FileViewerComponent {...props} />
    </ContentErrorBoundary>
  );
}

// eslint-disable-next-line complexity -- TODO fix later
const FileViewerComponent = React.memo(function FileViewerComponent({
  content,
  properties,
  loading,
  versionId,
  initialView,
  urlFragment,
  onViewChange,
  onSelectionAction,
  viewConfig,
  lineRange,
  jsonViewMode,
  s3Url,
}: FileViewerProps) {
  const fileType = getFileType(properties.extension, properties.name || properties.displayFileName);
  const contentSize = properties.size;
  const fileStatus = getFileStatus(fileType, contentSize, properties.extension);
  const parsedInitialView = useParsedInitialView({ urlFragment, versionId, initialView });

  // The text view opens automatically when the content will render legibly as
  // text; binary-looking content stays behind the explicit "View as text"
  // action. Either choice is remembered per file version.
  const versionKey = versionId ?? `name:${properties.name ?? properties.displayFileName ?? ""}`;
  const [textViewChoices, setTextViewChoices] = useState<ReadonlyMap<string, boolean>>(new Map());
  const canViewAsText =
    contentSize != null &&
    Number.isSafeInteger(contentSize) &&
    contentSize >= 0 &&
    contentSize <= Math.min(TEXT_FILE_DISPLAY_MAX_SIZE, fileTransferConfig.previewMaxBytes);
  const wantsTextFallback = (fileStatus === "not_previewable" || fileStatus === "unsupported") && canViewAsText;
  const contentLooksLikeText = useIsProbablyText(content, wantsTextFallback);
  const explicitTextViewChoice = textViewChoices.get(versionKey);
  const textViewActive = explicitTextViewChoice ?? contentLooksLikeText === true;

  const openTextView = () => setTextViewChoices((prev) => new Map(prev).set(versionKey, true));
  const closeTextView = () => setTextViewChoices((prev) => new Map(prev).set(versionKey, false));

  const viewAsTextAction = canViewAsText ? (
    <Button variant="outline" size="sm" onClick={openTextView}>
      <FileText />
      View as text
    </Button>
  ) : undefined;

  // TODO: the fragment encoding/decoding should be part of the specific viewer.
  const handleSelectionChange = useCallback(
    (action: SelectionAction) => {
      if (onSelectionAction) {
        onSelectionAction(action);
      }

      if (onViewChange && versionId) {
        let fragment = "";
        if (action.type === "line-range") {
          fragment = formatUrlFragment({
            versionId,
            codeSelection: { startLine: action.startLine, endLine: action.endLine },
          });
        } else if (action.type === "json-path") {
          fragment = formatUrlFragment({ versionId, jsonPath: action.path });
        } else if (action.type === "image-view") {
          fragment = formatUrlFragment({
            versionId,
            centerNormalized: action.centerNormalized,
            zoomRelativeToFit: action.zoomRelativeToFit,
          });
        }

        onViewChange(fragment);
      }
    },
    [onSelectionAction, onViewChange, versionId],
  );

  if (loading || fileStatus === "loading") {
    return (
      <StatusCard
        icon={<ThemedLoadingSpinner width="24px" height="24px" />}
        title="Loading file"
        description="Fetching content and preparing a preview..."
      />
    );
  }

  const contentMissing = !(
    (typeof content === "string" && content.length > 0) ||
    (Array.isArray(content) && content.length > 0) ||
    (content instanceof Uint8Array && content.length > 0)
  );

  if (fileStatus === "ready" && contentMissing) {
    return (
      <StatusCard
        icon={<FileWarning />}
        title="Preview not available"
        description="You might not have access to this file's content. Ask the owner to share it with you."
      />
    );
  }

  if (fileStatus === "not_previewable" || fileStatus === "unsupported") {
    const sniffPending = wantsTextFallback && !contentMissing && contentLooksLikeText === undefined;

    if (sniffPending && explicitTextViewChoice === undefined) {
      return (
        <StatusCard
          icon={<ThemedLoadingSpinner width="24px" height="24px" />}
          title="Loading file"
          description="Fetching content and preparing a preview..."
        />
      );
    }

    if (textViewActive && canViewAsText && !contentMissing) {
      return (
        <div className="relative flex h-full min-h-0 w-full flex-col">
          <div className="bg-muted border-border flex items-center gap-2 border-b px-3 py-1.5">
            <FileText className="text-muted-foreground size-3.5 shrink-0" />
            <span className="text-muted-foreground text-xs font-medium">Viewing as plain text</span>
            <Button variant="ghost" size="sm" className="ml-auto h-6 px-2 text-xs" onClick={closeTextView}>
              Close
            </Button>
          </div>
          <div className="relative min-h-0 flex-1">
            <TextFileViewer
              content={content!}
              properties={properties}
              versionId={versionId}
              initialView={parsedInitialView}
              onSelectionChange={handleSelectionChange}
            />
          </div>
        </div>
      );
    }

    if (fileStatus === "not_previewable") {
      return (
        <StatusCard
          icon={<FileTypeIcon extension={properties.extension} />}
          title="Preview not available"
          description={
            <>
              This file type can be securely uploaded, downloaded, and shared, but can NOT be viewed in the Istari
              platform.
              {contentSize && contentSize > 0 ? ` (${formatBytes(contentSize)})` : ""}
            </>
          }
          action={viewAsTextAction}
        />
      );
    }

    return (
      <StatusCard
        icon={<FileTypeIcon extension={properties.extension} />}
        title="File type not supported"
        description={`.${String(properties.extension || "").toLowerCase()} isn't supported for preview.`}
        action={viewAsTextAction}
      />
    );
  }

  if (fileStatus === "unimplemented") {
    return (
      <StatusCard
        icon={<AlertTriangle />}
        title="Viewer coming soon"
        description={`The ${fileType} viewer hasn't been implemented yet.`}
      />
    );
  }

  if (fileStatus === "too_large") {
    return (
      <StatusCard
        icon={<FileTypeIcon extension={properties.extension} />}
        title="File is too large to preview"
        description={contentSize && contentSize > 0 && `Size: ${formatBytes(contentSize)}`}
      />
    );
  }

  const Viewer = VIEWER_STRATEGY[fileType] || VIEWER_STRATEGY.file;

  return (
    <div className="relative h-full min-h-0 w-full">
      <Suspense
        fallback={
          <div role="status" aria-label="Loading preview" className="flex h-full w-full items-center justify-center">
            <ThemedLoadingSpinner width="24px" height="24px" />
          </div>
        }
      >
        <Viewer
          content={content!}
          properties={properties}
          versionId={versionId}
          initialView={parsedInitialView}
          onSelectionChange={handleSelectionChange}
          s3Url={s3Url}
          viewConfig={viewConfig}
          lineRange={lineRange}
          jsonViewMode={jsonViewMode}
        />
      </Suspense>
    </div>
  );
});

interface FileViewerComponentProps {
  content: string | Uint8Array;
  properties: FileProperties;
  versionId?: string;
  initialView?: ViewerPrefs;
  viewConfig?: ViewConfig | null;
  lineRange?: { startLine: number; endLine: number } | null;
  jsonViewMode?: JsonViewMode | null;
  s3Url?: string | null;
  onSelectionChange: (action: SelectionAction) => void;
}

type ViewerComponent =
  | React.ComponentType<FileViewerComponentProps>
  | React.LazyExoticComponent<React.ComponentType<FileViewerComponentProps>>;

const VIEWER_STRATEGY: Record<FileType, ViewerComponent> = {
  image: ImageFileViewer,
  pdf: PdfFileViewer,
  docx: DocxFileViewer,
  "3dviewer": ThreeDFileViewer,
  html: HtmlFileViewer,
  markdown: MarkdownFileViewer,
  text: TextFileViewer,
  xml: XmlFileViewer,
  file: UnsupportedViewer,
  json: JsonViewer,
  csv: CsvFileViewer,
};
